import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  FormHelperText,
  Chip,
  IconButton,
  Alert,
  Tabs,
  Tab,
  Paper,
  Checkbox,
  Autocomplete
} from '@mui/material';
import { Product, NewProduct } from '../models/Product';
import { ProductDisplayIngredient } from '../models/Product';
import { Category } from '../models/Category';
import { MenuIngredient, menuIngredientService } from '../services/menuIngredientService';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

const normalizeIngredientId = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

interface ProductDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (product: Product | NewProduct, imageFile?: File) => Promise<void>;
  product?: Product;
  categories: Category[];
}

const ProductDialog: React.FC<ProductDialogProps> = ({
  open,
  onClose,
  onSave,
  product,
  categories
}) => {
  // État pour les données du produit
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState<number>(0);
  const [priceXL, setPriceXL] = useState<number | undefined>(undefined);
  const [hasXL, setHasXL] = useState<boolean>(false);
  const [xlWarning, setXlWarning] = useState<string | null>(null);
  const [category, setCategory] = useState('');
  const [isAvailable, setIsAvailable] = useState(true);
  const [isFeatured, setIsFeatured] = useState(false);
  const [isOnPromotion, setIsOnPromotion] = useState(false); // New state for promotion status
  const [discountPrice, setDiscountPrice] = useState<number | undefined>(undefined); // New state for discount price
  const [compositionMode, setCompositionMode] = useState<'SIMPLE' | 'COMPOSITE'>('SIMPLE');
  const [ingredients, setIngredients] = useState<string[]>([]);
  const [displayIngredients, setDisplayIngredients] = useState<ProductDisplayIngredient[]>([]);
  const [newIngredient, setNewIngredient] = useState('');
  const [ingredientSearch, setIngredientSearch] = useState('');
  const [menuIngredients, setMenuIngredients] = useState<MenuIngredient[]>([]);
  const [creatingMenuIngredient, setCreatingMenuIngredient] = useState(false);
  const [draggedIngredientIndex, setDraggedIngredientIndex] = useState<number | null>(null);
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  
  // État pour la soumission et les erreurs
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  
  // État pour la navigation par onglets
  const [activeStep, setActiveStep] = useState(0);
  
  // Définition des onglets
  const tabs = [
    { label: 'Informations générales', optional: false },
    { label: 'Images et médias', optional: false },
    { label: 'Ingrédients et tags', optional: false },
  ];

  const syncDisplayIngredients = (items: ProductDisplayIngredient[]) => {
    const normalized = items
      .filter((item) => item.label.trim())
      .map((item, index) => ({ ...item, sortOrder: index }));
    setDisplayIngredients(normalized);
    setIngredients(normalized.map((item) => item.label));
  };

  const toDisplayIngredient = (
    label: string,
    index: number,
    existing?: ProductDisplayIngredient,
    menuIngredient?: MenuIngredient
  ): ProductDisplayIngredient => ({
    menuIngredientId:
      menuIngredient?.id ||
      existing?.menuIngredientId ||
      normalizeIngredientId(label) ||
      `ingredient_${index + 1}`,
    label,
    removable: existing?.removable ?? menuIngredient?.removableDefault ?? true,
    isDefaultIncluded: existing?.isDefaultIncluded ?? true,
    sortOrder: index,
  });

  // Initialiser l'état avec les données du produit si en mode édition
  useEffect(() => {
    if (product) {
      setName(product.name);
      setDescription(product.description || '');
      setPrice(product.price);
      setPriceXL(product.priceXL);
      setHasXL(product.priceXL !== undefined);
      
      // Trouver l'ID de la catégorie à partir du nom stocké dans le produit
      let matchedCategoryId = '';
      if (product.category) {
        // Chercher par nom de catégorie (cas normal)
        const nameMatch = categories.find(cat => 
          cat.name === product.category
        );
        if (nameMatch) {
          matchedCategoryId = nameMatch.id;
        } else {
          // Fallback: chercher par ID (pour compatibilité avec d'anciennes données)
          const idMatch = categories.find(cat => cat.id === product.category);
          if (idMatch) {
            matchedCategoryId = idMatch.id;
          } else {
            // Dernier recours: chercher en ignorant la casse
            const caseInsensitiveMatch = categories.find(cat => 
              cat.name.toLowerCase() === product.category.toLowerCase()
            );
            if (caseInsensitiveMatch) {
              matchedCategoryId = caseInsensitiveMatch.id;
            }
          }
        }
      }
      
      // Si aucune correspondance trouvée, utiliser la première catégorie disponible
      if (!matchedCategoryId && categories.length > 0) {
        matchedCategoryId = categories[0].id;
        console.warn(`Catégorie "${product.category}" non trouvée, utilisation de "${categories[0].name}" par défaut`);
      }
      
      setCategory(matchedCategoryId);
      setIsAvailable(product.isAvailable);
      setIsFeatured(product.isFeatured || false);
      const initialCompositionMode = product.compositionMode ||
        ((product.displayIngredients?.length || product.ingredients?.length) ? 'COMPOSITE' : 'SIMPLE');
      setCompositionMode(initialCompositionMode);
      const initialDisplayIngredients = product.displayIngredients?.length
        ? product.displayIngredients
        : (product.ingredients || []).map((label, index) => toDisplayIngredient(label, index));
      syncDisplayIngredients(initialCompositionMode === 'COMPOSITE' ? initialDisplayIngredients : []);
      setTags(product.tags || []);
      setImagePreview(product.imageUrl || null);
      setIsOnPromotion(product.isOnPromotion || false); // Initialize isOnPromotion
      setDiscountPrice(product.discountPrice); // Initialize discountPrice
    } else {
      // Réinitialiser l'état pour un nouveau produit
      setName('');
      setDescription('');
      setPrice(0);
      setPriceXL(undefined);
      setHasXL(false);
      setCategory(categories.length > 0 ? categories[0].id : '');
      setIsAvailable(true);
      setIsFeatured(false);
      setCompositionMode('SIMPLE');
      setIsOnPromotion(false); // Reset isOnPromotion
      setDiscountPrice(undefined); // Reset discountPrice
      syncDisplayIngredients([]);
      setTags([]);
      setImagePreview(null);
    }
    
    // Réinitialiser les états d'erreur et de fichier
    setError(null);
    setValidationErrors({});
    setImageFile(null);
    setNewIngredient('');
    setIngredientSearch('');
    setNewTag('');
    setActiveStep(0);
    
  }, [product, open, categories]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    menuIngredientService.list(true)
      .then((items) => {
        if (!cancelled) setMenuIngredients(items);
      })
      .catch(() => {
        if (!cancelled) setMenuIngredients([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Fonctions de gestion des champs
  // Ajout multiple d'ingrédients séparés par des virgules
  const handleAddIngredient = () => {
    if (!newIngredient.trim()) return;
    const existingLabels = new Set(displayIngredients.map((item) => normalizeIngredientId(item.label)));
    const newItems = newIngredient
      .split(',')
      .map(item => item.trim())
      .filter(item => {
        const key = normalizeIngredientId(item);
        return item.length > 0 && key && !existingLabels.has(key);
      });
    if (newItems.length > 0) {
      syncDisplayIngredients([
        ...displayIngredients,
        ...newItems.map((label, offset) => toDisplayIngredient(label, displayIngredients.length + offset)),
      ]);
    }
    setNewIngredient('');
  };

  const handleRemoveIngredient = (ingredient: string) => {
    syncDisplayIngredients(displayIngredients.filter(item => item.label !== ingredient));
  };

  const handleIngredientSelectionChange = (value: string[]) => {
    const existingByLabel = new Map(
      displayIngredients.map((item) => [normalizeIngredientId(item.label), item])
    );
    syncDisplayIngredients(
      Array.from(new Set(value.map((item) => item.trim()).filter(Boolean))).map((label, index) => {
        const menuIngredient = menuIngredients.find((item) => item.label === label);
        return toDisplayIngredient(label, index, existingByLabel.get(normalizeIngredientId(label)), menuIngredient);
      })
    );
  };

  const handleCreateMenuIngredientInline = async () => {
    const label = ingredientSearch.trim();
    if (!label || creatingMenuIngredient) return;
    setCreatingMenuIngredient(true);
    try {
      const created = await menuIngredientService.create(label);
      setMenuIngredients((current) => [...current, created]);
      syncDisplayIngredients([
        ...displayIngredients,
        toDisplayIngredient(created.label, displayIngredients.length, undefined, created),
      ]);
      setIngredientSearch('');
    } finally {
      setCreatingMenuIngredient(false);
    }
  };

  const toggleIngredientRemovable = (index: number) => {
    syncDisplayIngredients(
      displayIngredients.map((item, itemIndex) => (
        itemIndex === index ? { ...item, removable: !item.removable } : item
      ))
    );
  };

  const moveIngredient = (from: number, to: number) => {
    if (to < 0 || to >= displayIngredients.length) return;
    const next = [...displayIngredients];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    syncDisplayIngredients(next);
  };
  
  // Gestion des tags
  const handleAddTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setTags(tags.filter(item => item !== tag));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    setImageFile(file);

    // Créer un aperçu de l'image
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  // Validation du formulaire
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!name.trim()) errors.name = 'Le nom est requis';
    if (price <= 0) errors.price = 'Le prix doit être supérieur à 0';
    if (hasXL) {
      if (priceXL === undefined || priceXL <= 0) {
        errors.priceXL = 'Le prix XL doit être supérieur à 0';
      }
    }
    // Alerte non bloquante si XL <= prix normal
    if (hasXL && priceXL !== undefined && priceXL <= price) {
      setXlWarning('Attention: le prix XL est inférieur ou égal au prix normal');
    } else {
      setXlWarning(null);
    }
    if (!category || category.trim() === '' || categories.length === 0) {
      errors.category = categories.length === 0
        ? 'Les catégories ne sont pas encore chargées. Veuillez patienter.'
        : 'Veuillez sélectionner une catégorie avant d\'enregistrer';
    }
    
    // Validation pour le prix promotionnel si en promotion
    if (isOnPromotion) {
      if (!discountPrice || discountPrice <= 0) {
        errors.discountPrice = 'Le prix promotionnel est requis et doit être supérieur à 0';
      } else if (discountPrice >= price) {
        errors.discountPrice = 'Le prix promotionnel doit être inférieur au prix normal';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Soumission du formulaire
  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setSubmitting(true);
      setError(null);

      // Bloquer si les catégories ne sont pas chargées ou introuvables
      const selectedCategory = categories.find(cat => cat.id === category);
      if (!selectedCategory) {
        setValidationErrors(prev => ({
          ...prev,
          category: 'Catégorie introuvable ou non chargée'
        }));
        setSubmitting(false);
        return;
      }
      const catalogIsActive = product?.isActive ?? true;
      const catalogIsVisibleOnline = product?.isVisibleOnline ?? true;

      // Créer l'objet produit sans category (on ne l'ajoute que si elle a réellement changé)
      let productData: any = {
        name,
        description: description.trim(), // Toujours inclure la description, même si vide
        price,
        isAvailable,
        // La disponibilité temporaire ne doit pas masquer le produit du catalogue.
        isActive: catalogIsActive,
        isVisibleOnline: catalogIsVisibleOnline,
        isFeatured,
        compositionMode,
        ingredients: compositionMode === 'COMPOSITE' && ingredients.length > 0 ? ingredients : [],
        displayIngredients: compositionMode === 'COMPOSITE' ? displayIngredients : [],
        tags: tags.length > 0 ? tags : [],
      };

      // Inclure la catégorie seulement si elle a changé (ou en création)
      const categoryChanged = !product || product.category !== selectedCategory.id;
      if (categoryChanged) {
        productData.category = selectedCategory.id;
      }

      // Gérer Taille XL via case à cocher
      if (hasXL && priceXL !== undefined && priceXL > 0) {
        productData.priceXL = priceXL;
      } else if (product && product.priceXL !== undefined) {
        // Suppression explicite du champ s'il existait
        productData.priceXL = null;
      }

      // Ajouter l'id si en mode édition
      if (product) {
        productData = {
          id: product.id,
          ...productData
        };
      }

      // Add promotion fields if on promotion
      if (isOnPromotion) {
        productData.isOnPromotion = true;
        productData.discountPrice = discountPrice;
      } else {
        // Explicitly set to false and remove discountPrice if not on promotion
        productData.isOnPromotion = false;
        productData.discountPrice = undefined;
      }


      // Appeler la fonction de sauvegarde fournie par le parent
      await onSave(productData as Product | NewProduct, imageFile || undefined);
      onClose();
    } catch (err) {
      console.error('Erreur lors de la sauvegarde du produit:', err);
      setError('Une erreur est survenue lors de la sauvegarde. Veuillez réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {product ? 'Modifier le produit' : 'Ajouter un produit'}
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs
            value={activeStep}
            onChange={(_, nextTab) => setActiveStep(nextTab)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {tabs.map((tab) => (
              <Tab key={tab.label} label={tab.label} />
            ))}
          </Tabs>
        </Box>

        <Box role="tabpanel" hidden={activeStep !== 0}>
          {activeStep === 0 && (
              <Box sx={{ py: 1 }}>
                <TextField
                  fullWidth
                  label="Nom du produit"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  margin="normal"
                  error={!!validationErrors.name}
                  helperText={validationErrors.name}
                  required
                />

                <FormControl 
                  fullWidth 
                  margin="normal"
                  error={!!validationErrors.category}
                  required
                >
                  <InputLabel>Catégorie</InputLabel>
                  <Select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    label="Catégorie"
                  >
                    {categories.map((cat) => (
                      <MenuItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {validationErrors.category && (
                    <FormHelperText>{validationErrors.category}</FormHelperText>
                  )}
                </FormControl>

                <TextField
                  fullWidth
                  label="Prix (MAD)"
                  type="number"
                  value={price}
                  onChange={(e) => setPrice(Number(e.target.value))}
                  margin="normal"
                  inputProps={{ min: 0, step: 0.01 }}
                  error={!!validationErrors.price}
                  helperText={validationErrors.price}
                  required
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={hasXL}
                      onChange={(e) => {
                        setHasXL(e.target.checked);
                        if (!e.target.checked) {
                          setPriceXL(undefined);
                          setValidationErrors(prev => {
                            const { priceXL: _omit, ...rest } = prev;
                            return rest;
                          });
                          setXlWarning(null);
                        }
                      }}
                    />
                  }
                  label="Proposer une taille XL"
                />

                {hasXL && (
                  <TextField
                    fullWidth
                    label="Prix XL (MAD)"
                    type="number"
                    value={priceXL ?? ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      setPriceXL(val === '' ? undefined : Number(val));
                    }}
                    margin="normal"
                    inputProps={{ min: 0, step: 0.01 }}
                    error={!!validationErrors.priceXL}
                    helperText={validationErrors.priceXL || xlWarning || ''}
                    required
                  />
                )}

                <TextField
                  fullWidth
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  margin="normal"
                  multiline
                  rows={3}
                />

                <Box sx={{ display: 'flex', mt: 2, gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isAvailable}
                        onChange={(e) => setIsAvailable(e.target.checked)}
                      />
                    }
                    label="Disponible"
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={isFeatured}
                        onChange={(e) => setIsFeatured(e.target.checked)}
                      />
                    }
                    label="Produit vedette"
                  />
                </Box>

                {/* Promotion Fields */}
                <Box sx={{ display: 'flex', mt: 2, gap: 2, alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isOnPromotion}
                        onChange={(e) => {
                          setIsOnPromotion(e.target.checked);
                          // Reset discount price if promotion is turned off
                          if (!e.target.checked) {
                            setDiscountPrice(undefined);
                            setValidationErrors(prev => {
                              const { discountPrice: _, ...rest } = prev;
                              return rest;
                            });
                          }
                        }}
                      />
                    }
                    label="En promotion"
                  />

                  {isOnPromotion && (
                    <TextField
                      label="Prix promotionnel (MAD)"
                      type="number"
                      value={discountPrice ?? ''} // Use empty string for undefined to avoid React warnings
                      onChange={(e) => setDiscountPrice(Number(e.target.value))}
                      margin="normal"
                      size="small"
                      sx={{ width: 150 }}
                      inputProps={{ min: 0, step: 0.01 }}
                      error={!!validationErrors.discountPrice}
                      helperText={validationErrors.discountPrice}
                      required={isOnPromotion} // Make required only if on promotion
                    />
                  )}
                </Box>

              </Box>
          )}
        </Box>

        <Box role="tabpanel" hidden={activeStep !== 1}>
          {activeStep === 1 && (
              <Box sx={{ py: 1 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Image du produit
                </Typography>
                
                <Box
                  sx={{
                    border: '1px dashed grey',
                    borderRadius: 1,
                    p: 1,
                    mb: 2,
                    height: 300,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundImage: imagePreview ? `url(${imagePreview})` : 'none',
                    backgroundSize: 'contain',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                  }}
                >
                  {!imagePreview && (
                    <Button
                      variant="outlined"
                      component="label"
                    >
                      Choisir une image
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={handleImageChange}
                      />
                    </Button>
                  )}
                </Box>

                {imagePreview && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <Button
                      variant="outlined"
                      component="label"
                      size="small"
                      sx={{ mr: 1 }}
                    >
                      Changer
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={handleImageChange}
                      />
                    </Button>
                    
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => {
                        setImagePreview(null);
                        setImageFile(null);
                      }}
                    >
                      Supprimer
                    </Button>
                  </Box>
                )}
              </Box>
          )}
        </Box>

        <Box role="tabpanel" hidden={activeStep !== 2}>
          {activeStep === 2 && (
              <Box sx={{ py: 1 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Ingrédients
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Ajoutez les ingrédients utilisés dans votre produit.
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={compositionMode === 'COMPOSITE'}
                      onChange={(event) => {
                        const nextMode = event.target.checked ? 'COMPOSITE' : 'SIMPLE';
                        setCompositionMode(nextMode);
                        if (nextMode === 'SIMPLE') {
                          syncDisplayIngredients([]);
                        }
                      }}
                    />
                  }
                  label="Produit composé"
                />
                
                {compositionMode === 'SIMPLE' ? (
                  <Alert severity="info" sx={{ mb: 3 }}>
                    Produit simple: aucune composition client n'est affichée. Le nom du produit suffit.
                  </Alert>
                ) : (
                <Box sx={{ mb: 3 }}>
                  <Autocomplete
                    multiple
                    options={[
                      ...menuIngredients.map((item) => item.label),
                      ...ingredients.filter((item) => !menuIngredients.some((menuItem) => menuItem.label === item)),
                    ]}
                    value={ingredients}
                    inputValue={ingredientSearch}
                    onInputChange={(_, value) => setIngredientSearch(value)}
                    onChange={(_, value) => handleIngredientSelectionChange(value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="Catalogue ingrédients menu"
                        helperText="Sélection depuis le catalogue structuré. Le champ manuel reste disponible pendant la transition."
                      />
                    )}
                    sx={{ mb: 2 }}
                  />
                  {ingredientSearch.trim() && !menuIngredients.some((item) => normalizeIngredientId(item.label) === normalizeIngredientId(ingredientSearch)) && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => void handleCreateMenuIngredientInline()}
                      disabled={creatingMenuIngredient}
                      startIcon={creatingMenuIngredient ? undefined : <AddIcon />}
                      sx={{ mb: 2 }}
                    >
                      {creatingMenuIngredient ? 'Création...' : `Créer "${ingredientSearch.trim()}" dans le catalogue`}
                    </Button>
                  )}
                  <Box sx={{ display: 'flex', mb: 1 }}>
                    <TextField
                      size="small"
                      fullWidth
                      value={newIngredient}
                      onChange={(e) => setNewIngredient(e.target.value)}
                      placeholder="Nouvel ingrédient... (séparez par virgule pour en ajouter plusieurs)"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && newIngredient.trim()) {
                          e.preventDefault();
                          handleAddIngredient();
                        }
                      }}
                    />
                    
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleAddIngredient}
                      disabled={!newIngredient.trim()}
                      startIcon={<AddIcon />}
                      sx={{ ml: 1, whiteSpace: 'nowrap' }}
                    >
                      Ajouter
                    </Button>
                  </Box>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {displayIngredients.map((ingredient, index) => (
                      <Paper
                        key={`${ingredient.menuIngredientId}-${index}`}
                        variant="outlined"
                        draggable
                        onDragStart={() => setDraggedIngredientIndex(index)}
                        onDragOver={(event) => event.preventDefault()}
                        onDrop={() => {
                          if (draggedIngredientIndex != null) {
                            moveIngredient(draggedIngredientIndex, index);
                            setDraggedIngredientIndex(null);
                          }
                        }}
                        sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
                      >
                        <DragIndicatorIcon fontSize="small" color="disabled" />
                        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 140, flex: 1 }}>
                          {ingredient.label}
                        </Typography>
                        <FormControlLabel
                          control={(
                            <Switch
                              size="small"
                              checked={ingredient.removable}
                              onChange={() => toggleIngredientRemovable(index)}
                            />
                          )}
                          label={ingredient.removable ? 'Retirable' : 'Fixe'}
                        />
                        <Chip
                          label={ingredient.isDefaultIncluded ? 'Inclus' : 'Optionnel'}
                          size="small"
                          variant="outlined"
                        />
                        <IconButton size="small" onClick={() => handleRemoveIngredient(ingredient.label)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Paper>
                    ))}
                  </Box>
                </Box>
                )}
                
                <Typography variant="subtitle1" gutterBottom>
                  Tags
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Ajoutez des tags pour faciliter le filtrage et la recherche des produits.
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', mb: 1 }}>
                    <TextField
                      size="small"
                      fullWidth
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      placeholder="Nouveau tag..."
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && newTag.trim()) {
                          e.preventDefault();
                          handleAddTag();
                        }
                      }}
                    />
                    
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleAddTag}
                      disabled={!newTag.trim()}
                      startIcon={<AddIcon />}
                      sx={{ ml: 1, whiteSpace: 'nowrap' }}
                    >
                      Ajouter
                    </Button>
                  </Box>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {tags.map((tag, index) => (
                      <Chip
                        key={index}
                        label={tag}
                        onDelete={() => handleRemoveTag(tag)}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          Annuler
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || categories.length === 0}
        >
          {submitting ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductDialog;
